import { fetchArtistById, Person } from 'common/apiService';
import Layout from 'common/Layout/Layout';
import ContentList from 'common/ContentList';
import Head from 'next/head';
import { __t } from 'common/i18n';
import { GetServerSideProps } from 'next';
import ArtistHeader from 'common/ArtistHeader';
import xIPCountry from 'common/apiService/xIPCountry';
import * as Sentry from '@sentry/nextjs';
import Breadcrumb from 'common/Breadcrumb';
import { safeJsonLd } from 'common/utils/safeJsonLd';
import Seo, { SITE_URL } from 'common/Seo';

export default function SingleArtistPage(props: Person) {
  const { name, videos, summary, short_id, slug } = props;

  const breadcrumbs = [
    { name: __t('common.home'), url: '/' },
    { name: __t('content.artists'), url: '/contents' },
    { name, url: `/artists/${short_id}/${slug}` },
  ];

  const artistSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name,
    url: `${SITE_URL}/artists/${short_id}/${slug}`,
    ...(summary?.trim() && { description: summary }),
  };

  return (
    <>
      <Seo
        title={`${name} | ${__t('static_siteName')}`}
        description={summary?.trim() || undefined}
        path={`/artists/${short_id}/${slug}`}
        type="profile"
      />
      <Head>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(artistSchema) }} />
      </Head>
      <Breadcrumb items={breadcrumbs} />
      <ArtistHeader {...props} />
      <Layout disableTopGutter>
        {videos && <ContentList items={videos} />}
      </Layout>
    </>
  );
}

export const getServerSideProps: GetServerSideProps<Person, { id: string; }> = async ({ params, req, res }) => {
  xIPCountry({ req, res });
  const { id: shortId } = params ?? {};
  if (!shortId) return { notFound: true };

  try {
    const { data } = await fetchArtistById(shortId);

    return {
      props: data,
    };
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag('Status', error.response.status);
      scope.setTag('StatusText', error.response.statusText);
      scope.setTag('Url', error.response.config.url);
      Sentry.captureException(error.response);
    });
    if (error.response.status === 404) return { notFound: true };
    throw new Error(error);
  }
};
